import { flash } from "@xola/ui-kit";
import { DateFormat, formatDate } from "common";
import { isArray, isEmpty } from "lodash";
import pluralize from "pluralize";
import React from "react";
import { useMutation } from "react-query";
import { Event, Guide } from "types";
import { api } from "@/api/api";
import { PromptModal } from "@/components/Common/PromptModal";
import { useInvalidate } from "@/hooks/useInvalidation";
import { useGuides } from "@/hooks/useSellerResource";
import { getStart } from "@/models/Event";

interface UnassignEventGuidePromptProps {
    isOpen: boolean;
    events: Event[] | Event;
    onClose: (event: Event) => void;
    isMultiSelectEventsFromDashboard?: boolean;
}

export const UnassignEventGuidePrompt = ({
    isOpen,
    events,
    onClose,
    isMultiSelectEventsFromDashboard = false,
}: UnassignEventGuidePromptProps) => {
    const invalidate = useInvalidate();
    const isSingleEvent = !Array.isArray(events) && "id" in events;
    const event = isSingleEvent ? events : undefined;
    const { guides } = useGuides(null, { enabled: !!event, keepPreviousData: true });

    const { mutateAsync: unassignGuides, isLoading } = useMutation(async ({ event }: { event: Event }) => {
        for (const guide of event.guides) {
            try {
                await api.delete(`/events/${event.id}/guides/${guide.guide.id}`);
            } catch (error: any) {
                const text = `Error removing guide ${guide.guide.name}. ${
                    error?.response?.data?.field?.guide?.message ?? ""
                }`;

                flash.show({ text, color: "danger" });
                throw new Error(text);
            }
        }
    });

    if ((isEmpty(event?.guides) && !isArray(events)) || !isOpen) {
        return null;
    }

    const handleConfirm = async (event: Event) => {
        const eventsArray = Array.isArray(events) ? events : [events];

        try {
            for (const event of eventsArray) {
                await unassignGuides({ event });
                invalidate("events", event?.id);
            }
        } catch {
            // unassignGuides has already flashed the reason, so keep the prompt open for a retry rather than
            // letting the rejection escape the click handler.
            return;
        }

        invalidate("timeslots");
        onClose(event);
    };

    const getSingleEventModalContent = (event: Event) => {
        const confirmationCopy = `Do you want to unassign the ${pluralize("guide", event.guides.length)}?`;

        return (
            <div className="space-y-1">
                <p>
                    {getNoActiveReservation(event)}
                    {getGuideList(event, guides)}
                </p>
                <p>{confirmationCopy}</p>
            </div>
        );
    };

    const getMultiEventsModalContent = (events: Event[]) => {
        if (isMultiSelectEventsFromDashboard) {
            return "Are you sure you want to unassign all guides from the selected events?";
        }

        return (
            <div className="space-y-1">
                {events.map((event) => (
                    <p key={event.id}>{getNoActiveReservation(event)}</p>
                ))}
                <p>{`Do you want to unassign the guides from the above ${pluralize("event", events.length)}?`}</p>
            </div>
        );
    };

    const modalContent = isArray(events) ? getMultiEventsModalContent(events) : getSingleEventModalContent(events);

    return (
        <PromptModal
            title="Confirm Unassign"
            isOpen={isOpen}
            isLoading={isLoading}
            onConfirm={handleConfirm}
            onClose={onClose}
        >
            {modalContent}
        </PromptModal>
    );
};

const getNoActiveReservation = (event: Event) => (
    <>
        There are no active reservations in&nbsp;
        <var className="font-semibold" data-var="eventTitle">
            {event.title}
        </var>
        &nbsp;on&nbsp;
        <var data-var="startDate">{formatDate(getStart(event), DateFormat.DAY_DATE)}</var> at&nbsp;
        <var data-var="startTime">{formatDate(getStart(event), DateFormat.TIME)}</var>?&nbsp;
    </>
);

const getGuideList = (event: Event, guides: Guide[]) => {
    const guideNames: string[] = [];
    for (const guide of event.guides) {
        guideNames.push(guides.find((item) => item.id === guide.guide.id)?.name ?? "");
    }

    return (
        <>
            <var className="font-semibold" data-var="guideName">
                {guideNames.join(", ")}
            </var>{" "}
            {pluralize("is", guideNames.length)} assigned as {pluralize("guide", guideNames.length)} to this event.
        </>
    );
};
